/**
 * Crop Overlay Component
 * 
 * Visual overlay showing crop frame on VideoPlayer
 * Displays which area will be visible in target aspect ratio
 */

import React, { useEffect, useState } from 'react';

interface CropOverlayProps {
  isActive: boolean;
  sourceAspectRatio: string;
  targetAspectRatio: string;
  canvasDimensions: { width: number; height: number };
  selectedMediaItem?: {
    position?: { x: number; y: number };
    scale?: { scaleX: number; scaleY: number };
  } | null;
}

const CropOverlay: React.FC<CropOverlayProps> = ({
  isActive,
  sourceAspectRatio,
  targetAspectRatio,
  canvasDimensions,
  selectedMediaItem
}) => {
  const [cropFrame, setCropFrame] = useState({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!isActive || !canvasDimensions.width || !canvasDimensions.height) {
      return;
    }

    // Calculate crop frame dimensions
    const calculateCropFrame = () => {
      const { width: canvasWidth, height: canvasHeight } = canvasDimensions;

      // Parse aspect ratios
      const [targetW, targetH] = targetAspectRatio.split(':').map(Number);
      const targetRatio = targetW / targetH;

      const canvasRatio = canvasWidth / canvasHeight;

      let frameWidth: number;
      let frameHeight: number;

      // Calculate frame dimensions based on aspect ratio
      if (targetRatio > canvasRatio) {
        // Target is wider - use full width, crop height
        frameWidth = canvasWidth;
        frameHeight = canvasWidth / targetRatio;
      } else {
        // Target is taller - use full height, crop width
        frameHeight = canvasHeight;
        frameWidth = canvasHeight * targetRatio;
      }

      // CRITICAL: Frame stays CENTERED - no position or scale adjustments
      // The frame is FIXED, only the media underneath moves
      const frameX = (canvasWidth - frameWidth) / 2;
      const frameY = (canvasHeight - frameHeight) / 2;

      setCropFrame({
        x: frameX,
        y: frameY,
        width: frameWidth,
        height: frameHeight
      });
    };

    calculateCropFrame();
  }, [isActive, sourceAspectRatio, targetAspectRatio, canvasDimensions]); // Removed selectedMediaItem dependency

  if (!isActive) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
    >
      {/* Darkened overlay outside crop area */}
      <svg width="100%" height="100%" style={{ position: 'absolute' }}>
        <defs>
          <mask id="cropMask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={cropFrame.x}
              y={cropFrame.y}
              width={cropFrame.width}
              height={cropFrame.height}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#cropMask)"
        />
      </svg>

      {/* Crop frame - dark background, no border */}
      <div
        style={{
          position: 'absolute',
          left: `${cropFrame.x}px`,
          top: `${cropFrame.y}px`,
          width: `${cropFrame.width}px`,
          height: `${cropFrame.height}px`,
          backgroundColor: 'rgba(0, 0, 0, 0.3)', // Dark semi-transparent background
          border: '1px solid rgba(191, 35, 61, 0.61)', // Subtle border
          pointerEvents: 'none'
        }}
      >
        {/* Label */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '6px 14px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {targetAspectRatio} Output Area
        </div>
      </div>
    </div>
  );
};

export default CropOverlay;
